import { useEffect, useRef, useState } from 'react';

import { faSparkles } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import {
  faPause,
  faPlay,
  faShield,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@repo/videa-components/src/index';
import { useAtom, useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';

import { usePreferences } from '@/react-query/usePreferences';
import { formatPatientFirstNameAndLastName } from '@/utils/patient';

import { getPatientName } from '../../../uploader/patientList/patientListTableContainer/utils';
import {
  appointmentDataAtom,
  hasMicrophonePermissionAtom,
  isCancelRecordingConfirmationShownAtom,
  isRecorderSheetOpenAtom,
  isRecordingSessionActiveAtom,
  isRecordingSessionPausedAtom,
  mediaBlobUrlAtom,
  monitoringStreamAtom,
  previewAudioStreamAtom,
  shouldAllowRecordingAtom,
  shouldReopenRecorderSheetAtom,
} from '../../atoms';
import MicrophonePermissionFlow from '../../components/permissions/MicrophonePermissionFlow';
import TimerDisplay from '../../components/TimerDisplay';
import { buttonStyling } from '../../styles';

import { getAudioRecorder } from './audioRecorderControls';
import { ConsentFlow } from './ConsentFlow';
import { VoiceCard } from './VoiceCard';

interface AudioRecorderProps {
  onCancelRecording: () => void;
  onCancelRecordingConfirmation: () => void;
  onPauseRecording: () => void;
  onResumeRecording: () => void;
  onStartRecording: () => void;
  onStartProviderOnlyRecording: () => void;
  onGenerateNote: () => void;
  onPatientConsentDenied: () => void;
  isPromptSelected: boolean;
}

const AudioRecorder = ({
  onCancelRecording,
  onCancelRecordingConfirmation,
  onPauseRecording,
  onResumeRecording,
  onStartRecording,
  onStartProviderOnlyRecording,
  onGenerateNote,
  onPatientConsentDenied,
  isPromptSelected,
}: AudioRecorderProps) => {
  // atoms
  const appointmentData = useAtomValue(appointmentDataAtom);
  const isRecordingSessionActive = useAtomValue(isRecordingSessionActiveAtom);
  const isRecordingSessionPaused = useAtomValue(isRecordingSessionPausedAtom);
  const mediaBlobUrl = useAtomValue(mediaBlobUrlAtom);
  const previewAudioStream = useAtomValue(previewAudioStreamAtom);
  const [isSheetOpen, setIsSheetOpen] = useAtom(isRecorderSheetOpenAtom);
  const [shouldReopen, setShouldReopen] = useAtom(
    shouldReopenRecorderSheetAtom,
  );
  const [
    isCancelRecordingConfirmationShown,
    setIsCancelRecordingConfirmationShown,
  ] = useAtom(isCancelRecordingConfirmationShownAtom);
  const monitoringStream = useAtomValue(monitoringStreamAtom);
  const [hasMicrophonePermission, setHasMicrophonePermission] = useAtom(
    hasMicrophonePermissionAtom,
  );
  const { data: preferences } = usePreferences();

  // audio recording control interface
  const startRecording = () => getAudioRecorder().start?.();
  const stopRecording = () => getAudioRecorder().stop?.();
  const pauseRecording = () => getAudioRecorder().pause?.();
  const resumeRecording = () => getAudioRecorder().resume?.();
  const clearRecording = () => getAudioRecorder().clear?.();

  // local state
  const [isRecordingStopped, setIsRecordingStopped] = useState(false);
  const [isRecordingCanceled, setIsRecordingCanceled] = useState(false);
  const [isMicrophoneMuted, setIsMicrophoneMuted] = useState(true);

  // patient info
  const displayPhi = useSelector((state: any) => state.login?.user?.privacyOn);
  const patient = appointmentData?.patient;
  const patientName = getPatientName(
    formatPatientFirstNameAndLastName(patient?.firstName, patient?.lastName),
    0,
    displayPhi,
  );
  const patientId = patient?.pmsPatientId ? patient.pmsPatientId : 'N/A';

  const displayNoticeText = () => {
    if (!isRecordingSessionActive) return null;
    if (isCancelRecordingConfirmationShown) {
      return 'Recording paused - waiting for confirmation to cancel.';
    }
    if (isRecordingSessionPaused) {
      return 'Audio capture is paused.';
    }
    return 'Capturing audio...';
  };

  const displayNoticeDetailsText = () => {
    if (!isRecordingSessionActive) return null;
    if (isCancelRecordingConfirmationShown) {
      return "If you cancel now, you'll lose all progress on this recording.";
    }
    return "When you're done speaking, select Generate Note.";
  };

  const handleStartRecording = () => {
    try {
      startRecording();
      onStartRecording();
    } catch (err) {
      console.error('Failed to start recording:', err);
    }
  };

  const handleStartProviderOnlyRecording = () => {
    try {
      startRecording();
      onStartProviderOnlyRecording();
    } catch (err) {
      console.error('Failed to start recording:', err);
    }
  };

  /**
   * This visually transitions to the next "step" of this workflow,
   * where we can start recording audio via MediaStream Recording API.
   */
  const handlePauseRecordingButtonClick = () => {
    if (isRecordingSessionPaused) {
      resumeRecording();
      onResumeRecording();
    } else {
      pauseRecording();
      onPauseRecording();
    }
  };

  /**
   * This pauses the active recording session, and toggles on
   * the "cancel confirmation" button set in place of the default
   * audio recorder controls.
   */
  const handleCancelButtonClick = () => {
    pauseRecording();
    onCancelRecording();
    setIsCancelRecordingConfirmationShown(true);
  };

  /**
   * This is effective the "go back" action if a user does not want
   * to commit to actually canceling the active recording.
   */
  const handleNotNowButtonClick = () => {
    resumeRecording();
    setIsCancelRecordingConfirmationShown(false);
  };

  /**
   * This stops recording audio via MediaStream Recording API.
   * As soon as `stopRecording()` finishes running, the value of `mediaBlobUrl`
   * should no longer be `undefined`, which will bubble back up to the parent.
   */
  const handleStopRecording = () => {
    setIsRecordingStopped(true);
    setIsRecordingCanceled(false);
    stopRecording();
  };

  /**
   * This is the "cancel confirmation" action, which stops the active
   * recording, fires off an async request to clear the mediaBlobUrl,
   * hides the "cancel confirm" button set and invokes the onCancelRecording
   * callback.
   */
  const handleCancelRecordingConfirmationButtonClick = () => {
    setIsRecordingStopped(true);
    setIsRecordingCanceled(true);
    setIsCancelRecordingConfirmationShown(false);
    stopRecording();
    if (!isSheetOpen) {
      setShouldReopen(true);
    }
  };

  const handleConsentDeniedClick = () => {
    setIsSheetOpen(false);
    onPatientConsentDenied();
  };

  // store the latest version of onGenerateNote in a ref
  const onGenerateNoteRef = useRef(onGenerateNote);
  const onCancelRecordingRef = useRef(onCancelRecording);
  const onCancelRecordingConfirmationRef = useRef(
    onCancelRecordingConfirmation,
  );

  useEffect(() => {
    onGenerateNoteRef.current = onGenerateNote;
  }, [onGenerateNote]);

  useEffect(() => {
    onCancelRecordingRef.current = onCancelRecording;
  }, [onCancelRecording]);

  useEffect(() => {
    onCancelRecordingConfirmationRef.current = onCancelRecordingConfirmation;
  }, [onCancelRecordingConfirmation]);

  useEffect(() => {
    if (shouldReopen) {
      setIsSheetOpen(true);
      setShouldReopen(false);
    }
  }, [shouldReopen, setIsSheetOpen, setShouldReopen]);

  useEffect(() => {
    if (!isRecordingStopped) return;

    if (isRecordingCanceled) {
      if (mediaBlobUrl !== undefined) {
        clearRecording();
        onCancelRecordingConfirmationRef.current();
        setIsRecordingStopped(false);
        setIsRecordingCanceled(false);
      }
    } else if (mediaBlobUrl !== undefined) {
      onGenerateNoteRef.current();
      clearRecording();
      setIsRecordingStopped(false);
    }
  }, [isRecordingStopped, isRecordingCanceled, mediaBlobUrl]);

  return (
    <div className="vai-flex vai-flex-col vai-items-center vai-space-y-3 vai-w-full">
      {!isSheetOpen && (
        <div className="vai-text-center">
          <p className="vai-text-xl">{patientName}</p>
          <p className="vai-typography-muted vai-text-xs vai-text-slate-800/50">
            ID: {patientId}
          </p>
        </div>
      )}

      {!hasMicrophonePermission && (
        <MicrophonePermissionFlow
          onPermissionGranted={() => setHasMicrophonePermission(true)}
        />
      )}

      {hasMicrophonePermission && (
        <>
          <div className="vai-flex vai-items-center vai-justify-center vai-mb-4">
            <div
              className={`vai-flex vai-items-center vai-gap-1 vai-px-4 vai-py-4 ${isRecordingSessionActive && isSheetOpen ? 'vai-w-full' : ''}`}
            >
              {/* Timer Display - only show when recording */}
              {isRecordingSessionActive && isSheetOpen && (
                <div className="vai-w-1/2 vai-flex vai-justify-center vai-pr-4">
                  <TimerDisplay />
                </div>
              )}
              {/* Microphone Visualizer */}
              <div className="vai-flex vai-items-center">
                <VoiceCard
                  stream={monitoringStream || previewAudioStream}
                  onMuteStateChange={setIsMicrophoneMuted}
                  isRecordingSessionActive={isRecordingSessionActive}
                />
              </div>
            </div>
          </div>

          {isRecordingSessionActive && isSheetOpen && (
            <div className="vai-flex vai-flex-col vai-items-center vai-mt-2">
              <p className="vai-text-xl vai-font-bold">{displayNoticeText()}</p>
              <p className="vai-text-sm vai-text-muted-foreground vai-pt-2">
                {displayNoticeDetailsText()}
              </p>
            </div>
          )}

          <div className="vai-mt-2">
            {!isRecordingSessionActive ? (
              <ConsentFlow
                onConsentDenied={handleConsentDeniedClick}
                onStartProviderOnlyRecording={handleStartProviderOnlyRecording}
                onStartRecording={handleStartRecording}
                isPromptSelected={isPromptSelected}
                monitoringStream={monitoringStream}
                mediaBlobUrl={mediaBlobUrl}
                isSheetOpen={isSheetOpen}
                buttonStyling={buttonStyling}
                enableVoiceNotesConsent={
                  preferences?.enableVoiceNotesConsent ?? false
                }
              />
            ) : (
              <>
                <div className="vai-flex vai-space-x-3">
                  {!isCancelRecordingConfirmationShown && (
                    <>
                      <Button
                        variant="ghost"
                        onClick={handleCancelButtonClick}
                        size={isSheetOpen ? 'default' : 'sm'}
                        disabled={isCancelRecordingConfirmationShown}
                      >
                        <span>
                          <FontAwesomeIcon icon={faXmark} /> Cancel
                        </span>
                      </Button>

                      {isRecordingSessionPaused ? (
                        <Button
                          variant="sheet"
                          onClick={handlePauseRecordingButtonClick}
                          className={`hover:vai-ring-slate-200/50`}
                          size={isSheetOpen ? 'default' : 'sm'}
                        >
                          <span>
                            <FontAwesomeIcon icon={faPlay} /> Resume
                          </span>
                        </Button>
                      ) : (
                        <Button
                          variant="sheet"
                          onClick={handlePauseRecordingButtonClick}
                          className={`${buttonStyling} hover:vai-ring-slate-200/50`}
                          size={isSheetOpen ? 'default' : 'sm'}
                        >
                          <span>
                            <FontAwesomeIcon icon={faPause} /> Pause
                          </span>
                        </Button>
                      )}
                      <Button
                        onClick={handleStopRecording}
                        variant={'pulse'}
                        size={isSheetOpen ? 'default' : 'sm'}
                      >
                        <span>
                          <FontAwesomeIcon icon={faSparkles} />
                        </span>
                        Generate Note
                      </Button>
                    </>
                  )}

                  {/* Cancel confirmation appears at bottom when triggered */}
                  {isCancelRecordingConfirmationShown && (
                    <>
                      <Button
                        variant="destructive"
                        onClick={handleCancelRecordingConfirmationButtonClick}
                        className="vai-px-6 vai-bg-red-600 hover:vai-bg-red-700"
                      >
                        Cancel Recording
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleNotNowButtonClick}
                        className="vai-px-6"
                      >
                        Not Now
                      </Button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AudioRecorder;
